import type { RGB, ColorState, WCAGEvaluation, ComplianceLevel } from '../types/contrast'

// ============================================
// Color Parsing & Validation
// ============================================

export function isValidHex(hex: string): boolean {
  return /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)
}

export function isValidRgb(r: number, g: number, b: number): boolean {
  return [r, g, b].every(v => Number.isInteger(v) && v >= 0 && v <= 255)
}

export function hexToRgb(hex: string): RGB | null {
  const cleanHex = hex.replace(/^#/, '')
  const fullHex = cleanHex.length === 3
    ? cleanHex.split('').map(c => c + c).join('')
    : cleanHex

  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(fullHex)

  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : null
}

export function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number): string => {
    const clamped = Math.max(0, Math.min(255, Math.round(n)))
    return clamped.toString(16).padStart(2, '0')
  }
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

export function parseColor(input: string): ColorState {
  const trimmed = input.trim()

  // Try HEX format
  if (isValidHex(trimmed)) {
    const rgb = hexToRgb(trimmed)
    if (rgb) {
      return {
        hex: rgbToHex(rgb.r, rgb.g, rgb.b),
        rgb,
        isValid: true
      }
    }
  }

  // Try RGB format: rgb(r, g, b) or r, g, b
  const rgbMatch = trimmed.match(/^(?:rgb\s*\(\s*)?(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)?$/i)
  if (rgbMatch) {
    const r = parseInt(rgbMatch[1], 10)
    const g = parseInt(rgbMatch[2], 10)
    const b = parseInt(rgbMatch[3], 10)
    if (isValidRgb(r, g, b)) {
      return {
        hex: rgbToHex(r, g, b),
        rgb: { r, g, b },
        isValid: true
      }
    }
  }

  return {
    hex: trimmed,
    rgb: { r: 0, g: 0, b: 0 },
    isValid: false
  }
}

// Create a ColorState from RGB values
export function createColorFromRgb(r: number, g: number, b: number): ColorState {
  if (!isValidRgb(r, g, b)) {
    return {
      hex: '',
      rgb: { r: 0, g: 0, b: 0 },
      isValid: false
    }
  }
  return {
    hex: rgbToHex(r, g, b),
    rgb: { r, g, b },
    isValid: true
  }
}

// Create a ColorState from HEX value
export function createColorFromHex(hex: string): ColorState {
  return parseColor(hex)
}

// ============================================
// WCAG Contrast Calculation
// ============================================

/**
 * Calculate relative luminance according to WCAG 2.x specification
 * @see https://www.w3.org/WAI/WCAG21/Understanding/contrast-minimum.html
 */
export function getRelativeLuminance(rgb: RGB): number {
  const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(v => {
    const sRGB = v / 255
    return sRGB <= 0.04045
      ? sRGB / 12.92
      : Math.pow((sRGB + 0.055) / 1.055, 2.4)
  })

  // ITU-R BT.709 coefficients
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/**
 * Calculate contrast ratio between two colors
 * @returns Contrast ratio (1:1 to 21:1)
 */
export function getContrastRatio(foreground: RGB, background: RGB): number {
  const L1 = getRelativeLuminance(foreground)
  const L2 = getRelativeLuminance(background)

  const lighter = Math.max(L1, L2)
  const darker = Math.min(L1, L2)

  // WCAG contrast ratio formula
  return (lighter + 0.05) / (darker + 0.05)
}

export function formatContrastRatio(ratio: number): string {
  return `${ratio.toFixed(2)}:1`
}

// ============================================
// WCAG Compliance Evaluation
// ============================================

const WCAG_THRESHOLDS = {
  AA_NORMAL_TEXT: 4.5,
  AA_LARGE_TEXT: 3,
  AA_UI_COMPONENTS: 3,
  AAA_NORMAL_TEXT: 7,
  AAA_LARGE_TEXT: 4.5
} as const

function createComplianceLevel(actual: number, required: number): ComplianceLevel {
  return {
    passes: actual >= required,
    requiredRatio: required,
    actualRatio: actual
  }
}

/**
 * Evaluate contrast against all WCAG criteria
 */
export function evaluateContrast(foreground: RGB, background: RGB): WCAGEvaluation {
  const ratio = getContrastRatio(foreground, background)
  // Round to 2 decimal places for display, but use full precision for comparison
  const roundedRatio = Math.round(ratio * 100) / 100

  return {
    contrastRatio: roundedRatio,
    formattedRatio: formatContrastRatio(roundedRatio),
    wcagAA: {
      normalText: createComplianceLevel(ratio, WCAG_THRESHOLDS.AA_NORMAL_TEXT),
      largeText: createComplianceLevel(ratio, WCAG_THRESHOLDS.AA_LARGE_TEXT),
      uiComponents: createComplianceLevel(ratio, WCAG_THRESHOLDS.AA_UI_COMPONENTS)
    },
    wcagAAA: {
      normalText: createComplianceLevel(ratio, WCAG_THRESHOLDS.AAA_NORMAL_TEXT),
      largeText: createComplianceLevel(ratio, WCAG_THRESHOLDS.AAA_LARGE_TEXT)
    }
  }
}

// ============================================
// Helper Functions
// ============================================

/**
 * Determine if text should be considered "large" per WCAG
 * Large text: ≥18pt (24px) regular OR ≥14pt (18.67px) bold
 */
export function isLargeText(size: 'normal' | 'large', weight: 'normal' | 'bold'): boolean {
  return size === 'large' || (size === 'normal' && weight === 'bold')
}

/**
 * Get the required contrast ratio based on text configuration
 */
export function getRequiredRatio(
  size: 'normal' | 'large',
  weight: 'normal' | 'bold',
  level: 'AA' | 'AAA',
  elementType: 'text' | 'ui-component'
): number {
  if (elementType === 'ui-component') {
    return WCAG_THRESHOLDS.AA_UI_COMPONENTS
  }

  const isLarge = isLargeText(size, weight)

  if (level === 'AAA') {
    return isLarge ? WCAG_THRESHOLDS.AAA_LARGE_TEXT : WCAG_THRESHOLDS.AAA_NORMAL_TEXT
  }

  return isLarge ? WCAG_THRESHOLDS.AA_LARGE_TEXT : WCAG_THRESHOLDS.AA_NORMAL_TEXT
}

// ============================================
// Color Suggestions
// ============================================

export interface ColorSuggestion {
  color: ColorState
  contrastRatio: number
  direction: 'darker' | 'lighter'
}

/**
 * Suggest an accessible foreground color that meets the target contrast ratio
 * Adjusts the foreground color by making it darker or lighter
 */
export function suggestAccessibleColor(
  foreground: RGB,
  background: RGB,
  targetRatio: number
): ColorSuggestion | null {
  const currentRatio = getContrastRatio(foreground, background)

  // Already passes
  if (currentRatio >= targetRatio) {
    return null
  }

  // Try darkening the foreground (for light backgrounds)
  const darkerSuggestion = findAccessibleColor(foreground, background, targetRatio, 'darker')

  // Try lightening the foreground (for dark backgrounds)
  const lighterSuggestion = findAccessibleColor(foreground, background, targetRatio, 'lighter')

  // Choose the suggestion that's closer to the original color
  if (darkerSuggestion && lighterSuggestion) {
    const darkerDistance = colorDistance(foreground, darkerSuggestion.color.rgb)
    const lighterDistance = colorDistance(foreground, lighterSuggestion.color.rgb)
    return darkerDistance <= lighterDistance ? darkerSuggestion : lighterSuggestion
  }

  // Return whichever one works
  return darkerSuggestion || lighterSuggestion
}

function findAccessibleColor(
  foreground: RGB,
  background: RGB,
  targetRatio: number,
  direction: 'darker' | 'lighter'
): ColorSuggestion | null {
  const step = direction === 'darker' ? -5 : 5
  let testRgb = { ...foreground }

  for (let i = 0; i < 60; i++) { // Max 60 iterations (300 units of change)
    testRgb = {
      r: Math.max(0, Math.min(255, testRgb.r + step)),
      g: Math.max(0, Math.min(255, testRgb.g + step)),
      b: Math.max(0, Math.min(255, testRgb.b + step))
    }

    const ratio = getContrastRatio(testRgb, background)
    if (ratio >= targetRatio) {
      return {
        color: {
          hex: rgbToHex(testRgb.r, testRgb.g, testRgb.b),
          rgb: testRgb,
          isValid: true
        },
        contrastRatio: Math.round(ratio * 100) / 100,
        direction
      }
    }

    // Stop if we've hit the color boundary
    if (direction === 'darker' && testRgb.r === 0 && testRgb.g === 0 && testRgb.b === 0) break
    if (direction === 'lighter' && testRgb.r === 255 && testRgb.g === 255 && testRgb.b === 255) break
  }

  return null
}

function colorDistance(a: RGB, b: RGB): number {
  return Math.sqrt(
    Math.pow(a.r - b.r, 2) +
    Math.pow(a.g - b.g, 2) +
    Math.pow(a.b - b.b, 2)
  )
}

// ============================================
// Default Colors
// ============================================

export const DEFAULT_FOREGROUND: ColorState = {
  hex: '#000000',
  rgb: { r: 0, g: 0, b: 0 },
  isValid: true
}

export const DEFAULT_BACKGROUND: ColorState = {
  hex: '#FFFFFF',
  rgb: { r: 255, g: 255, b: 255 },
  isValid: true
}
